import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { resolveAnchorElement } from "../../services/intent/anchorTargets";
import { NudgeAction } from "../../services/intent/types";
import AppButton from "../reusableComponent/button/button";
import styles from "./IntentCoachMark.module.scss";

interface IntentCoachMarkProps {
  nudge: NudgeAction;
  onDismiss: () => void;
  onCta: () => void;
}

const AUTO_DISMISS_MS = 12000;
const FADE_MS = 200;
const BUBBLE_GAP_PX = 12;
const TOP_SAFE_AREA_PX = 64;

const isInViewport = (rect: DOMRect): boolean =>
  rect.bottom > 0 &&
  rect.top < window.innerHeight &&
  rect.right > 0 &&
  rect.left < window.innerWidth;

const IntentCoachMark: React.FC<IntentCoachMarkProps> = ({ nudge, onDismiss, onCta }) => {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const placementRef = useRef<"above" | "below" | null>(null);
  const rafRef = useRef<number | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [isFading, setIsFading] = useState(false);

  const fadeAndDismiss = useCallback(() => {
    if (fadingRef.current) return;
    fadingRef.current = true;
    setIsFading(true);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    fadeTimerRef.current = setTimeout(onDismiss, FADE_MS);
  }, [onDismiss]);

  const updatePosition = useCallback(() => {
    const target = targetRef.current;
    const bubble = bubbleRef.current;
    if (!target || !bubble) return;
    const rect = target.getBoundingClientRect();
    if (!isInViewport(rect)) {
      fadeAndDismiss();
      return;
    }
    const bubbleRect = bubble.getBoundingClientRect();
    const aboveTop = rect.top - bubbleRect.height - BUBBLE_GAP_PX;
    if (placementRef.current === null) {
      const requested = target.getAttribute("data-coach-placement") === "below" ? "below" : "above";
      placementRef.current = requested === "above" && aboveTop < TOP_SAFE_AREA_PX ? "below" : requested;
    }
    const top =
      placementRef.current === "below"
        ? rect.bottom + BUBBLE_GAP_PX
        : aboveTop;
    if (top < TOP_SAFE_AREA_PX) {
      fadeAndDismiss();
      return;
    }
    const left = rect.left + rect.width / 2 - bubbleRect.width / 2;
    bubble.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    if (!ready) setReady(true);
  }, [fadeAndDismiss, ready]);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      updatePosition();
    });
  }, [updatePosition]);

  useEffect(() => {
    if (!nudge.target) {
      console.warn(`[intent] coach-mark missing target`);
      onDismiss();
      return;
    }
    const target = resolveAnchorElement(nudge.target);
    if (!target) {
      console.warn(`[intent] coach-mark target not found`, nudge.target);
      onDismiss();
      return;
    }
    targetRef.current = target;
    updatePosition();
    window.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    };
  }, [nudge.target, onDismiss, scheduleUpdate, updatePosition]);

  useEffect(() => {
    dismissTimerRef.current = setTimeout(fadeAndDismiss, AUTO_DISMISS_MS);
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [fadeAndDismiss]);

  useEffect(() => {
    const handleOutside = (e: MouseEvent): void => {
      const bubble = bubbleRef.current;
      if (bubble && !bubble.contains(e.target as Node)) fadeAndDismiss();
    };
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") fadeAndDismiss();
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleKey);
    };
  }, [fadeAndDismiss]);

  const handleCtaClick = (): void => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    onCta();
  };

  const bubble = (
    <div
      ref={bubbleRef}
      className={`${styles.bubble} ${isFading ? styles.bubbleFading : ""}`}
      role="status"
      aria-live="polite"
      style={{ visibility: ready ? "visible" : "hidden" }}
    >
      <div className={styles.text}>{nudge.text}</div>
      {nudge.cta?.label && (
        <AppButton variant="newTertiary" size="sm" onClick={handleCtaClick} className={styles.cta}>
          {nudge.cta.label}
        </AppButton>
      )}
    </div>
  );

  return createPortal(bubble, document.body);
};

export default IntentCoachMark;
